import React, { useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import CustomerInfoForm from './CustomerInfoForm'
import CustomerSearch from './CustomerSearch'
import CaptureFlow from './CaptureFlow'
import TransactionCompletion from './TransactionCompletion'
import ScanIdButton from './ScanIdButton'
import { db, storage } from '../../../firebase'
import { collection, doc, runTransaction, serverTimestamp, updateDoc } from 'firebase/firestore'
import { ref, uploadString, getDownloadURL } from 'firebase/storage'
import OperatorPinDialog from '../../../components/auth/OperatorPinDialog'
import { logGoldEvent } from '../lib/goldAudit'
import { buildGoldPurchaseSnapshot, getCaptureMediaInfo, localDateKey, validateOptionalDateOfBirth } from '../lib/goldPurchaseSnapshot.mjs'
import { writeLocalPurchaseJson } from '../lib/localPurchaseStorage.mjs'

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1'])
const LOCAL_TX_KEY = 'artgold.mock.exchange.transactions.v1'
const LOCAL_SEQ_KEY_PREFIX = 'artgold.mock.exchange.txseq.'

// Staged flow, same order as the shop app: envelope → customer → capture → done.
const MACRO_STEPS = [
  { id: 'envelope', label: 'Envelope', icon: 'Package' },
  { id: 'customer', label: 'Customer', icon: 'User' },
  { id: 'capture', label: 'Capture', icon: 'Camera' },
  { id: 'success', label: 'Done', icon: 'CheckCircle' },
]

function getIsLocalMockMode() {
  if (typeof window === 'undefined') return false
  return LOCAL_HOSTNAMES.has(window.location.hostname)
}

function readLocalJson(key, fallback) {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

// Map a stored gold_customers doc onto the transaction customer form.
function customerFromRecord(record) {
  if (!record) return {}
  const nameParts = (record.name || '').trim().split(/\s+/)
  return {
    firstName: record.firstName || nameParts[0] || '',
    lastName: record.lastName || nameParts.slice(1).join(' ') || '',
    dateOfBirth: record.dateOfBirth || record.date_of_birth || record.dob || '',
    phone: record.phone || '',
    email: record.email || '',
    idType: record.idType || '',
    idNumber: record.idNumber || '',
    idState: record.idState || '',
    idCountry: record.idCountry || '',
    idExpiration: record.idExpiration || '',
    idOtherReason: record.idOtherReason || '',
    idOtherDetails: record.idOtherDetails || '',
    street: record.street || record.addressStreet || record.address || '',
    city: record.city || record.addressCity || '',
    state: record.state || record.addressState || '',
    zip: record.zip || record.addressZip || '',
    notes: record.notes || '',
  }
}

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00'
  return `$${value.toFixed(2)}`
}

const TransactionFlow = ({
  calculations: incomingCalculations,
  prices: incomingPrices,
  items: incomingItems,
  onComplete,
  onCancel
}) => {
  const isLocalMockMode = useMemo(() => getIsLocalMockMode(), [])
  // The price feed may refresh behind this dialog. Freeze the purchase the
  // operator started, including its calculated item payouts, without repricing.
  const [confirmedPurchase] = useState(() => {
    try {
      const snapshot = buildGoldPurchaseSnapshot({ items: incomingItems, calculations: incomingCalculations })
      return {
        snapshot,
        prices: { ...incomingPrices },
        calculations: { ...snapshot.totals },
      }
    } catch (err) {
      return { error: err?.message || 'The purchase calculation is invalid.' }
    }
  })
  const calculations = confirmedPurchase.calculations
  const prices = confirmedPurchase.prices || {}
  const items = confirmedPurchase.snapshot?.items || []
  // Shop-app parity: the operator PIN is asked per transaction — once to START
  // (gate below, flow closes on cancel) and again to CONFIRM the final save.
  // Both are server-verified; the saved doc records who did each.
  const [pinGate, setPinGate] = useState(confirmedPurchase.error ? null : 'start') // 'start' | 'confirm' | null
  // Mirrors pinGate synchronously so a PIN verification that resolves AFTER
  // the gate was cancelled is discarded instead of firing a stale-closure save.
  const pinGateRef = useRef(confirmedPurchase.error ? null : 'start')
  const setGate = (gate) => { pinGateRef.current = gate; setPinGate(gate) }
  const [txOperator, setTxOperator] = useState(null)
  const [pendingCaptures, setPendingCaptures] = useState(null)
  const [step, setStep] = useState('envelope')
  const [envelopeNumber, setEnvelopeNumber] = useState('')
  const [customer, setCustomer] = useState({})
  const [existingCustomer, setExistingCustomer] = useState(null)
  const [isCustomerValid, setIsCustomerValid] = useState(false)
  // AI ID scan: the scan photo is reused as the ID photo in the capture step.
  const [idScanPhoto, setIdScanPhoto] = useState(null)
  const [scanNotice, setScanNotice] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [transactionId, setTransactionId] = useState(null)
  const [transactionNumber, setTransactionNumber] = useState('')
  const [completedPhoto, setCompletedPhoto] = useState({ src: null, skipped: false })
  const [error, setError] = useState(null)

  // The flow owns the screen; the page behind must not scroll (that scroll is
  // what made the page drift around under fingers mid-transaction).
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const handleExistingCustomer = (record) => {
    setExistingCustomer(record)
    if (record) {
      setCustomer((prev) => ({ ...prev, ...customerFromRecord(record) }))
    }
  }

  // Map the AI reader's fields (shop-app contract) onto the customer form.
  const applyIdScan = (fields, photoDataUrl) => {
    const idTypeMap = {
      driver_license: 'driver_license',
      state_id: 'identification_card',
      passport: 'passport',
      military_id: 'military_id',
      immigration_card: 'immigration_card',
      other: 'other',
    }
    const firstName = [fields.first_name, fields.middle_name].filter(Boolean).join(' ')
    const lastName = [fields.last_name, fields.name_suffix].filter(Boolean).join(' ')

    setCustomer((prev) => ({
      ...prev,
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(fields.date_of_birth ? { dateOfBirth: fields.date_of_birth } : {}),
      idType: idTypeMap[fields.doc_type] || prev.idType || 'other',
      ...(fields.id_number ? { idNumber: fields.id_number } : {}),
      ...(fields.id_state ? { idState: fields.id_state } : {}),
      ...(fields.id_country ? { idCountry: fields.id_country } : {}),
      ...(fields.expiration ? { idExpiration: fields.expiration } : {}),
      ...(fields.id_other_reason ? { idOtherReason: fields.id_other_reason } : {}),
      ...(fields.id_other_details ? { idOtherDetails: fields.id_other_details } : {}),
      ...(fields.address_street ? { street: fields.address_street } : {}),
      ...(fields.address_city ? { city: fields.address_city } : {}),
      ...(fields.address_state ? { state: fields.address_state } : {}),
      ...(fields.address_zip ? { zip: fields.address_zip } : {}),
    }))
    if (photoDataUrl) setIdScanPhoto(photoDataUrl)

    const attention = [...(fields.missing_fields || []), ...(fields.low_confidence_fields || [])]
    setScanNotice(
      attention.length
        ? `Filled from the ID — please double-check: ${attention.join(', ')}`
        : 'Filled from the ID — please confirm the details below.'
    )
  }

  // Upload capture to Firebase Storage
  const uploadCapture = async (dataUrl, path) => {
    const storageRef = ref(storage, path)
    await uploadString(storageRef, dataUrl, 'data_url')
    return getDownloadURL(storageRef)
  }

  // Capture flow finished → hold the captures and ask for the confirm PIN.
  // The save only runs after a verified operator confirms (shop-app parity);
  // cancelling keeps the operator on the capture step with nothing lost.
  const handleCaptureComplete = (captures, captureDetails = {}) => {
    setError(null)
    setPendingCaptures({ captures, captureDetails })
    setGate('confirm')
  }

  const saveTransaction = async (captures, confirmedBy, captureDetails = {}) => {
    setSubmitting(true)
    setError(null)

    try {
      // Prepare transaction data
      const purchaseDate = new Date()
      if (confirmedPurchase.error) throw new Error(confirmedPurchase.error)
      const dateOfBirthError = validateOptionalDateOfBirth(customer.dateOfBirth, localDateKey(purchaseDate))
      if (dateOfBirthError) throw new Error(dateOfBirthError)

      // Validate every media type BEFORE creating a record; retain the existing
      // capture keys so older contracts keep working, with stable item metadata.
      const media = Object.entries(captures || {}).filter(([, dataUrl]) => !!dataUrl).map(([key, dataUrl]) => {
        const { mimeType, extension } = getCaptureMediaInfo(dataUrl)
        const itemMatch = /^item_photo_(\d+)$/.exec(key)
        const itemIndex = itemMatch ? Number(itemMatch[1]) : null
        const item = itemIndex == null ? null : items[itemIndex]
        if (itemMatch && !item) throw new Error('An item photo does not match this purchase.')
        const labels = { id_front: 'ID photo', customer_photo: 'Customer photo', signature_seller: 'Seller signature', signature_buyer: 'Buyer signature' }
        return {
          key, dataUrl, mimeType,
          fileName: `${key}.${extension}`,
          itemId: item?.itemId || null,
          label: item ? `Item ${itemIndex + 1}: ${item.description || item.itemType || item.metal}` : (labels[key] || key),
        }
      })
      const captureMetadata = Object.fromEntries(media.map(({ key, dataUrl, ...metadata }) => [key, metadata]))
      const transactionData = {
        envelopeNumber,
        // Purchases enter "To report". Only the reporting-confirmation service
        // can start the hold; creating or printing a purchase does not report it.
        dateKey: purchaseDate.toISOString().slice(0, 10),
        reportStatus: 'pending',
        reportedAt: null,
        holdStartedAt: null,
        holdUntil: null,
        reportConfirmation: null,
        reportDelivery: null,
        // Repeat customers keep their migrated/permanent id for history joins.
        customerId: existingCustomer?.id || null,
        customer: {
          name: `${customer.firstName} ${customer.lastName}`.trim(),
          firstName: customer.firstName,
          lastName: customer.lastName,
          dateOfBirth: customer.dateOfBirth || null,
          phone: customer.phone || null,
          email: customer.email || null,
          idType: customer.idType,
          idNumber: customer.idNumber || null,
          idState: customer.idState || null,
          idCountry: customer.idCountry || null,
          idExpiration: customer.idExpiration || null,
          idOtherReason: customer.idOtherReason || null,
          idOtherDetails: customer.idOtherDetails || null,
          street: customer.street || null,
          city: customer.city || null,
          state: customer.state || null,
          zip: customer.zip || null,
        },
        items,
        totals: { ...confirmedPurchase.snapshot.totals },
        prices: {
          gold: prices.gold,
          silver: prices.silver,
          source: prices.source,
        },
        notes: customer.notes || null,
        captureMetadata,
        customerPhotoSkipped: !!captureDetails.customerPhotoSkipped,
        status: 'completed',
        contractGenerated: false,
      }

      // Create transaction + assign a collision-free transaction number.
      const year = new Date().getFullYear()
      let docId = ''
      let txNumber = ''

      if (isLocalMockMode) {
        const seqKey = `${LOCAL_SEQ_KEY_PREFIX}${year}`
        const seq = Math.max(0, Number(readLocalJson(seqKey, 0)) || 0) + 1
        writeLocalPurchaseJson(seqKey, seq)
        txNumber = `AG-${year}-${String(seq).padStart(6, '0')}`
        docId = `local_tx_${Date.now().toString(36)}`

        const map = readLocalJson(LOCAL_TX_KEY, {}) || {}
        map[docId] = {
          id: docId,
          transactionNumber: txNumber,
          ...transactionData,
          operator: confirmedBy,
          startedBy: txOperator,
          captures, // data URLs in local mode
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        writeLocalPurchaseJson(LOCAL_TX_KEY, map)
      } else {
        const counterRef = doc(db, 'counters', `gold_transactions_${year}`)
        const txRef = doc(collection(db, 'gold_transactions'))

        await runTransaction(db, async (t) => {
          const snap = await t.get(counterRef)
          const current = snap.exists() ? Number(snap.data()?.seq || 0) : 0
          const next = Math.max(0, current) + 1
          txNumber = `AG-${year}-${String(next).padStart(6, '0')}`
          t.set(counterRef, { seq: next, updatedAt: serverTimestamp() }, { merge: true })
          t.set(txRef, {
            ...transactionData,
            operator: confirmedBy,
            startedBy: txOperator,
            transactionNumber: txNumber,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        })

        docId = txRef.id

        // Upload captures to Storage and store public download URLs.
        const capturePromises = media.map(async ({ key, dataUrl, fileName }) => {
          const path = `gold_transactions/${docId}/captures/${fileName}`
          const url = await uploadCapture(dataUrl, path)
          return { key, url }
        })

        const uploadedCaptures = await Promise.all(capturePromises)
        const captureUrls = {}
        uploadedCaptures.forEach(item => {
          if (item) captureUrls[item.key] = item.url
        })

        await updateDoc(doc(db, 'gold_transactions', docId), {
          captures: captureUrls,
          updatedAt: serverTimestamp(),
        })
      }

      setTransactionId(docId)
      setTransactionNumber(txNumber)
      setCompletedPhoto({ src: captures?.customer_photo || null, skipped: !!captureDetails.customerPhotoSkipped })

      if (!isLocalMockMode) {
        logGoldEvent('transaction_created', {
          transactionNumber: txNumber,
          envelopeNumber,
          payout: calculations.totalNet,
          weightGrams: calculations.totalWeight,
          operator: confirmedBy?.name || null,
          startedBy: txOperator?.name || null,
        })
      }

      setStep('success')
    } catch (err) {
      console.error('Transaction error:', err)
      setError(err?.message || 'Failed to save transaction. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const stepIndex = MACRO_STEPS.findIndex((s) => s.id === step)

  const renderStepBody = () => {
    if (confirmedPurchase.error) {
      return (
        <div className="space-y-4 py-4">
          <p role="alert" className="text-sm text-destructive">{confirmedPurchase.error}</p>
          <p className="text-sm text-muted-foreground">Return to the calculator and check the items before starting this purchase.</p>
          <Button variant="outline" onClick={onCancel}>Back to calculator</Button>
        </div>
      )
    }
    if (step === 'envelope') {
      return (
        <div className="space-y-6">
          <div className="text-center pt-4">
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Icon name="Package" size={26} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Envelope Number</h3>
            <p className="text-sm text-muted-foreground mt-1">Required for tracking the items</p>
          </div>

          <input
            type="text"
            value={envelopeNumber}
            onChange={(e) => setEnvelopeNumber(e.target.value)}
            placeholder="e.g., 000123"
            autoFocus
            className="w-full px-4 py-4 border border-border/60 rounded-xl text-2xl font-semibold text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />

          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" size="lg" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={() => setStep('customer')}
              disabled={!envelopeNumber.trim()}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continue
            </Button>
          </div>
        </div>
      )
    }

    if (step === 'customer') {
      return (
        <div className="space-y-5">
          <ScanIdButton onResult={applyIdScan} />
          {scanNotice && (
            <p className="text-sm text-primary flex items-start gap-1.5">
              <Icon name="Sparkles" size={14} className="mt-0.5 flex-shrink-0" />
              {scanNotice}
            </p>
          )}

          <div>
            <p className="text-sm font-medium text-foreground mb-2">Returning customer?</p>
            <CustomerSearch
              selected={existingCustomer}
              onSelect={handleExistingCustomer}
              showNewCustomer={false}
            />
          </div>

          <div className="pt-4 border-t border-border/60">
            <p className="text-sm font-medium text-foreground mb-3">
              {existingCustomer ? 'Confirm the details' : 'Customer details'}
            </p>
            <CustomerInfoForm
              customer={customer}
              onUpdate={setCustomer}
              onValidate={setIsCustomerValid}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border/60">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setStep('envelope')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={() => setStep('capture')}
              disabled={!isCustomerValid}
              iconName="Camera"
              iconPosition="right"
            >
              Capture
            </Button>
          </div>
        </div>
      )
    }

    if (step === 'capture') {
      return (
        <div className="space-y-4">
          {submitting ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-4" />
              <p className="text-muted-foreground">Saving transaction...</p>
            </div>
          ) : (
            <CaptureFlow
              items={items}
              customerName={`${customer.firstName} ${customer.lastName}`.trim()}
              initialCaptures={idScanPhoto ? { id_front: idScanPhoto } : undefined}
              onComplete={handleCaptureComplete}
              onCancel={() => setStep('customer')}
            />
          )}

          {error && (
            <div className="p-3 bg-destructive/15 border border-destructive/30 rounded-lg text-sm text-destructive">
              <Icon name="AlertCircle" size={16} className="inline mr-2" />
              {error}
            </div>
          )}
        </div>
      )
    }

    if (step === 'success') {
      return (
        <TransactionCompletion
          transactionId={transactionId}
          transactionNumber={transactionNumber}
          payout={calculations.totalNet}
          customerPhoto={completedPhoto.src}
          customerPhotoSkipped={completedPhoto.skipped}
          onPreview={() => {
            if (transactionId) window.open(`/exchange-desk/contract/${transactionId}`, '_blank', 'noopener,noreferrer')
          }}
          onPrint={() => {
            if (transactionId) window.open(`/exchange-desk/contract/${transactionId}?print=1`, '_blank', 'noopener,noreferrer')
          }}
          onNewTransaction={() => onComplete?.(transactionId, transactionNumber)}
        />
      )
    }

    return null
  }

  // Full-screen staged flow: full-bleed sheet on phones, centered card from sm:.
  return (
    <div className="fixed inset-0 z-[80] bg-background sm:bg-black/50 overflow-y-auto overscroll-contain">
      <div className="min-h-full sm:flex sm:items-start sm:justify-center sm:p-6">
        <div className="w-full sm:max-w-xl bg-background sm:rounded-2xl sm:border sm:border-border/60 sm:shadow-elevation-4 flex flex-col min-h-dvh sm:min-h-0">
          {/* Header: title, payout, progress steps */}
          <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 sm:px-6 sm:rounded-t-2xl">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground truncate">New Transaction</h2>
                <p className="text-sm text-muted-foreground">
                  Payout <span className="font-semibold text-primary">{formatCurrency(calculations?.totalNet)}</span>
                  {envelopeNumber ? <span> · Envelope {envelopeNumber}</span> : null}
                </p>
              </div>
              {step !== 'success' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onCancel}
                  aria-label="Cancel transaction"
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              {MACRO_STEPS.map((s, i) => {
                const isDone = i < stepIndex
                const isCurrent = i === stepIndex
                return (
                  <React.Fragment key={s.id}>
                    {i > 0 && (
                      <div className={`h-0.5 flex-1 rounded-full ${isDone || isCurrent ? 'bg-primary/50' : 'bg-muted/70'}`} />
                    )}
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDone
                            ? 'bg-primary text-primary-foreground'
                            : isCurrent
                              ? 'bg-primary/15 text-primary border border-primary/40'
                              : 'bg-muted/60 text-muted-foreground'
                        }`}
                      >
                        <Icon name={isDone ? 'Check' : s.icon} size={14} />
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          isCurrent ? 'text-foreground' : 'text-muted-foreground'
                        } ${isCurrent ? '' : 'hidden sm:inline'}`}
                      >
                        {s.label}
                      </span>
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </div>

          {/* Step body */}
          <div className="flex-1 px-4 py-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-5">
            {renderStepBody()}
          </div>
        </div>
      </div>

      {/* Per-transaction PIN gates (shop-app parity). CaptureFlow stays mounted
          behind the confirm gate so cancelling loses nothing. */}
      {pinGate === 'start' && (
        <OperatorPinDialog
          title="Start Transaction"
          subtitle="Enter your operator PIN to begin"
          submitLabel="Start"
          mockOperator={isLocalMockMode ? { id: 'local_mock', name: 'Local Test' } : null}
          onVerified={(op) => {
            if (pinGateRef.current !== 'start') return
            setTxOperator(op)
            setGate(null)
          }}
          onCancel={() => { setGate(null); onCancel?.() }}
        />
      )}
      {pinGate === 'confirm' && (
        <OperatorPinDialog
          title="Confirm Transaction"
          subtitle={`Payout ${formatCurrency(calculations?.totalNet)} — enter your PIN to save`}
          submitLabel="Confirm & Save"
          mockOperator={isLocalMockMode ? { id: 'local_mock', name: 'Local Test' } : null}
          onVerified={(op) => {
            if (pinGateRef.current !== 'confirm') return
            const pending = pendingCaptures
            if (!pending) return
            setPendingCaptures(null)
            setGate(null)
            saveTransaction(pending.captures, op, pending.captureDetails)
          }}
          onCancel={() => { setGate(null); setPendingCaptures(null) }}
        />
      )}
    </div>
  )
}

export default TransactionFlow
